import {BannerDismissedStore} from './bannerDismissed';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Whether `value` looks like a managed banner's ID, as opposed to a legacy banner type. */
export const isBannerId = (value: string) => UUID.test(value);

/**
 * The dismissal store without managed banners that are no longer eligible, so
 * IDs of ended banners don't pile up. Legacy banner types are kept for the
 * calendar-driven banners, which share the store. Returns `store` itself when
 * nothing is removed.
 */
export const pruneDismissedBanners = (
  store: BannerDismissedStore,
  eligibleIds: Iterable<string>
): BannerDismissedStore => {
  const eligible = new Set(eligibleIds);
  const stale = Object.keys(store).filter((key) => isBannerId(key) && !eligible.has(key));
  if (!stale.length) {
    return store;
  }
  const pruned = {...store};
  stale.forEach((key) => delete pruned[key]);
  return pruned;
};

/**
 * The banner to show: the shortest scheduled one the viewer hasn't dismissed,
 * picked at random among ties. Undefined when there's none.
 */
export const pickManagedBanner = <T extends {id: string; duration_seconds: number}>(
  eligible: readonly T[],
  dismissed: BannerDismissedStore,
  random: () => number = Math.random
): T | undefined => {
  const candidates = eligible.filter((banner) => !dismissed[banner.id]);
  if (!candidates.length) {
    return undefined;
  }
  const shortest = Math.min(...candidates.map((banner) => banner.duration_seconds));
  const tied = candidates.filter((banner) => banner.duration_seconds === shortest);
  return tied[Math.min(Math.floor(random() * tied.length), tied.length - 1)];
};
